import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';

/** Sección de un documento legal: título + cuerpo. */
export function Sec({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="font-display text-lg font-semibold text-content">{titulo}</h2>
      <div className="flex flex-col gap-2 font-sans text-sm leading-relaxed text-content-muted">
        {children}
      </div>
    </section>
  );
}

interface LegalShellProps {
  titulo: string;
  actualizado: string;
  intro: string;
  children: ReactNode;
  /** Enlace al otro documento legal (para navegación cruzada). */
  otro: { href: string; texto: string };
}

/**
 * Marco público (sin sesión) para documentos legales: Política de Privacidad y
 * Condiciones del Servicio. Requeridos por la pantalla de consentimiento de Google.
 */
export function LegalShell({ titulo, actualizado, intro, children, otro }: LegalShellProps) {
  useSurfaceTheme('bolsillo', 'light');

  return (
    <div className="min-h-screen bg-surface-base">
      <div className="h-1 w-full bg-brand-gradient" aria-hidden="true" />
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-6">
        <Link to="/" aria-label="Ir al inicio de ÁGORA">
          <img src="/brand/logopositivo.png" alt="ÁGORA" className="h-8 w-auto" />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 font-sans text-sm font-medium text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Volver
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16">
        <h1 className="font-display text-3xl font-bold text-content">{titulo}</h1>
        <p className="mt-2 font-sans text-sm text-content-muted">
          Última actualización: {actualizado}
        </p>
        <p className="mt-6 font-sans text-base leading-relaxed text-content">{intro}</p>

        <div className="mt-8 flex flex-col gap-7">{children}</div>

        <footer className="mt-12 flex flex-col items-start gap-4 border-t border-border pt-6">
          <div className="flex flex-wrap gap-x-6 gap-y-2 font-sans text-sm">
            <Link to={otro.href} className="font-medium text-primary hover:underline">
              {otro.texto}
            </Link>
            <Link to="/login" className="font-medium text-primary hover:underline">
              Iniciar sesión
            </Link>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-3 py-1 font-sans text-xs font-medium text-content-muted">
            <Sparkles className="h-3.5 w-3.5 text-violet" aria-hidden="true" />
            Powered by Grupo InteligencIA
          </span>
        </footer>
      </main>
    </div>
  );
}
